import { client } from "../../amplify-client";

export const PRESENCE_ONLINE_THRESHOLD_MS = 210_000;
// AppSync Events keepalive ping cadence while the tab is visible. Viewers
// treat a session with no ping for PRESENCE_SESSION_STALE_MS as dead, so the
// stale window must comfortably exceed the ping interval (one dropped ping
// must not flip a healthy user offline).
export const PRESENCE_PING_INTERVAL_MS = 60_000;
export const PRESENCE_SESSION_STALE_MS = 150_000;
// DynamoDB last_seen_at cadence while visible. Must stay inside
// PRESENCE_ONLINE_THRESHOLD_MS — the push processor and non-live viewers
// still derive online-ness from last_seen_at freshness.
export const PRESENCE_DDB_HEARTBEAT_MS = 180_000;

const PRESENCE_MIN_HEARTBEAT_GAP_MS = 10_000;
const PRESENCE_FAILURE_BACKOFF_MS = 90_000;
// A tab close should make the user appear offline to others without waiting
// out PRESENCE_ONLINE_THRESHOLD_MS. We push last_seen_at backwards by this
// many ms when the page is hidden so isUserOnline immediately returns false.
const PRESENCE_OFFLINE_BACKDATE_MS = PRESENCE_ONLINE_THRESHOLD_MS + 60_000;
const PRESENCE_SESSION_SWEEP_MS = 30_000;

function normalizeString(value) {
  return String(value || "").trim();
}

function maxIso(leftIso, rightIso) {
  const left = normalizeString(leftIso);
  const right = normalizeString(rightIso);
  if (!left) return right;
  if (!right) return left;
  return left > right ? left : right;
}

function errorMessagesFromResponse(resp) {
  return (resp?.errors ?? [])
    .map((error) => normalizeString(error?.message))
    .filter(Boolean);
}

function isLikelyRecordMissing(errorMessages) {
  const joined = String(errorMessages || "").toLowerCase();
  return (
    joined.includes("not found") ||
    joined.includes("does not exist") ||
    joined.includes("conditionalcheckfailed") ||
    joined.includes("no item")
  );
}

function isLikelyAlreadyExists(errorMessages) {
  const joined = String(errorMessages || "").toLowerCase();
  return (
    joined.includes("already exists") ||
    joined.includes("conditionalcheckfailed") ||
    joined.includes("duplicate")
  );
}

export function isUserOnline(lastSeenAt, nowMs = Date.now(), thresholdMs = PRESENCE_ONLINE_THRESHOLD_MS) {
  const ts = Date.parse(String(lastSeenAt || ""));
  if (!Number.isFinite(ts)) return false;
  return nowMs - ts <= thresholdMs;
}

export async function upsertMyPresence(userId, heartbeatIso = new Date().toISOString()) {
  const normalizedUserId = normalizeString(userId);
  const normalizedHeartbeatIso = normalizeString(heartbeatIso);
  if (!normalizedUserId || !normalizedHeartbeatIso) return;

  const updateInput = {
    user_id: normalizedUserId,
    last_seen_at: normalizedHeartbeatIso,
    updated_at: normalizedHeartbeatIso,
  };

  const initialUpdate = await client.models.UserPresence.update(updateInput);
  const initialUpdateErrors = errorMessagesFromResponse(initialUpdate);
  if (!initialUpdateErrors.length) {
    return initialUpdate?.data || null;
  }

  // Most failures here are first-write races where presence does not exist yet.
  const createResp = await client.models.UserPresence.create({
    user_id: normalizedUserId,
    last_seen_at: normalizedHeartbeatIso,
    updated_at: normalizedHeartbeatIso,
  });
  const createErrors = errorMessagesFromResponse(createResp);
  if (!createErrors.length) {
    return createResp?.data || null;
  }

  // Handle write races by retrying update using a monotonic timestamp.
  if (isLikelyRecordMissing(initialUpdateErrors) || isLikelyAlreadyExists(createErrors)) {
    const fallbackIso = maxIso(
      normalizedHeartbeatIso,
      new Date(Date.parse(normalizedHeartbeatIso) + 1).toISOString()
    );
    const retryUpdate = await client.models.UserPresence.update({
      user_id: normalizedUserId,
      last_seen_at: fallbackIso,
      updated_at: fallbackIso,
    });
    const retryErrors = errorMessagesFromResponse(retryUpdate);
    if (!retryErrors.length) {
      return retryUpdate?.data || null;
    }
  }

  throw new Error([...initialUpdateErrors, ...createErrors].join(", "));
}

// Pushes the user's last_seen_at far enough into the past that other clients
// treat them as offline immediately. Called from `pagehide` so a tab close
// surfaces "Offline" without waiting for the heartbeat window to lapse.
// Best effort — the browser may abort the request mid-flight, in which case
// the heartbeat decay still resolves the status within ~5 minutes.
export function markPresenceOffline(userId) {
  const normalizedUserId = normalizeString(userId);
  if (!normalizedUserId) return undefined;

  const offlineIso = new Date(Date.now() - PRESENCE_OFFLINE_BACKDATE_MS).toISOString();
  client.models.UserPresence.update({
    user_id: normalizedUserId,
    last_seen_at: offlineIso,
    updated_at: new Date().toISOString(),
  }).catch((error) => {
    console.warn("markPresenceOffline: best-effort write failed:", error);
  });
}

export async function listPresenceByUserIds(userIds = []) {
  const uniqueIds = [...new Set((userIds || []).map(normalizeString).filter(Boolean))];
  if (!uniqueIds.length) return new Map();

  // Presence is read through the band-gated `presenceForUsers` query rather
  // than direct UserPresence model reads: the model no longer grants cross-user
  // reads (that allowed harvesting anyone's online/last-seen across age bands).
  // The query returns rows only for users the caller may interact with; out-of-
  // band ids are omitted and surface as Offline, exactly as before.
  try {
    const resp = await client.queries.presenceForUsers({ userIds: uniqueIds });
    if (resp?.errors?.length) {
      throw new Error(resp.errors.map((error) => error.message).join(", "));
    }
    const rows = Array.isArray(resp?.data) ? resp.data : [];
    const presenceById = new Map();
    for (const row of rows) {
      const userId = normalizeString(row?.userId);
      if (!userId) continue;
      // Preserve the legacy row shape (`last_seen_at`) that callers read.
      presenceById.set(userId, { last_seen_at: row?.lastSeenAt || null });
    }
    return presenceById;
  } catch (error) {
    console.error("Failed to fetch user presence:", error);
    return new Map();
  }
}

// ---------------------------------------------------------------------------
// Live presence over AppSync Events.
//
// Each signed-in client publishes its own state to `presence/<cognitoSub>`
// (the backend's presence-namespace handler rejects publishes to anyone
// else's channel). Viewers subscribe to counterparts' channels for instant
// online/offline flips; DynamoDB last_seen_at remains the slow persistence
// layer for initial fetches and the push processor.
//
// Multi-device: every publisher tags events with a per-tab/per-launch
// sessionId. Viewers track freshness per session and report a user online
// while ANY session is alive — a phone going to background must not flip a
// user whose browser tab is still active.
// ---------------------------------------------------------------------------

function presenceChannelPath(userId) {
  return `presence/${normalizeString(userId)}`;
}

function makePresenceSessionId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

async function connectPresenceChannel(userId) {
  const { events } = await import("aws-amplify/data");
  return events.connect(presenceChannelPath(userId));
}

// AppSync Events has delivered subscription payloads in slightly different
// envelopes across SDK versions (same probing the user-channel listener
// does). Returns null unless the payload is a well-formed presence event.
export function extractPresenceEvent(data) {
  const evt = data?.event ?? data;
  const payload =
    evt && typeof evt === "object" && "payload" in evt ? evt.payload : evt;
  if (!payload || typeof payload !== "object") return null;
  if (normalizeString(payload.type) !== "presence") return null;
  const state = normalizeString(payload.state);
  if (state !== "online" && state !== "offline") return null;
  return {
    state,
    sessionId: normalizeString(payload.sessionId) || "unknown-session",
    at: normalizeString(payload.at) || new Date().toISOString(),
  };
}

/**
 * Publishes this user's presence (transitions + keepalive pings) and keeps
 * the UserPresence row fresh. Replaces the old write-every-45s heartbeat:
 * DynamoDB now sees a write on start, on visibility transitions, and every
 * PRESENCE_DDB_HEARTBEAT_MS while visible — everything "live" travels over
 * the events channel instead.
 *
 * Returns a stop() teardown, same contract as before.
 */
export function startPresenceHeartbeat({ userId } = {}) {
  const normalizedUserId = normalizeString(userId);
  if (!normalizedUserId) {
    return () => {};
  }

  const sessionId = makePresenceSessionId();
  let stopped = false;
  let channel = null;
  let connecting = null;
  let pingTimerId = null;
  let ddbTimerId = null;
  let lastHeartbeatIso = "";
  let lastSendMs = 0;
  let consecutiveFailures = 0;

  function isVisible() {
    return typeof document === "undefined" || document.visibilityState !== "hidden";
  }

  async function ensureChannel() {
    if (stopped || channel) return channel;
    if (!connecting) {
      connecting = connectPresenceChannel(normalizedUserId)
        .then((connected) => {
          connecting = null;
          if (stopped) {
            try {
              connected?.close?.();
            } catch {}
            return null;
          }
          channel = connected;
          return channel;
        })
        .catch((error) => {
          connecting = null;
          // DynamoDB writes keep threshold-based status working until the
          // next connect attempt (visibility transition or ping tick).
          console.error("Presence channel connect failed:", error);
          return null;
        });
    }
    return connecting;
  }

  async function publishPresence(state) {
    const target = channel ?? (await ensureChannel());
    if (!target || stopped) return;
    try {
      await target.publish({
        type: "presence",
        state,
        sessionId,
        at: new Date().toISOString(),
      });
    } catch (error) {
      // Force a reconnect on the next publish — the socket may have died.
      channel = null;
      console.error("Presence publish failed:", error);
    }
  }

  function buildMonotonicHeartbeatIso() {
    const nowIso = new Date().toISOString();
    const nextIso = maxIso(lastHeartbeatIso, nowIso);
    if (nextIso === lastHeartbeatIso) {
      const bumped = new Date(Date.parse(lastHeartbeatIso) + 1).toISOString();
      lastHeartbeatIso = bumped;
      return bumped;
    }
    lastHeartbeatIso = nextIso;
    return nextIso;
  }

  const sendHeartbeat = async ({ force = false } = {}) => {
    if (stopped) return;
    const nowMs = Date.now();
    if (!force && nowMs - lastSendMs < PRESENCE_MIN_HEARTBEAT_GAP_MS) return;

    const heartbeatIso = buildMonotonicHeartbeatIso();
    try {
      await upsertMyPresence(normalizedUserId, heartbeatIso);
      lastSendMs = Date.now();
      consecutiveFailures = 0;
    } catch (error) {
      consecutiveFailures += 1;
      console.error("Presence heartbeat failed:", error);
    }
  };

  function clearTimers() {
    if (pingTimerId) {
      clearInterval(pingTimerId);
      pingTimerId = null;
    }
    if (ddbTimerId) {
      clearTimeout(ddbTimerId);
      ddbTimerId = null;
    }
  }

  function scheduleNextDdbHeartbeat() {
    if (stopped || !isVisible()) return;
    if (ddbTimerId) clearTimeout(ddbTimerId);
    const delayMs = consecutiveFailures > 0
      ? Math.max(PRESENCE_DDB_HEARTBEAT_MS, PRESENCE_FAILURE_BACKOFF_MS)
      : PRESENCE_DDB_HEARTBEAT_MS;
    ddbTimerId = setTimeout(async () => {
      ddbTimerId = null;
      await sendHeartbeat();
      scheduleNextDdbHeartbeat();
    }, delayMs);
  }

  function startVisibleTimers() {
    clearTimers();
    if (stopped || !isVisible()) return;
    pingTimerId = setInterval(() => {
      void publishPresence("online");
    }, PRESENCE_PING_INTERVAL_MS);
    scheduleNextDdbHeartbeat();
  }

  function goVisible() {
    void publishPresence("online");
    void sendHeartbeat({ force: true });
    startVisibleTimers();
  }

  function goHidden() {
    clearTimers();
    void publishPresence("offline");
    // One accurate last_seen_at write so "Last seen N min ago" starts from
    // the moment the tab was hidden, not the previous heartbeat tick.
    void sendHeartbeat({ force: true });
  }

  const onVisibility = () => {
    if (stopped) return;
    if (isVisible()) {
      goVisible();
    } else {
      goHidden();
    }
  };

  const onFocus = () => {
    if (stopped) return;
    void publishPresence("online");
    void sendHeartbeat({ force: true });
  };

  // pagehide is the most reliable signal for "user is leaving this tab"
  // (close, navigation, browser quit). We fire-and-forget a presence
  // update that backdates last_seen_at so the user shows offline to peers
  // immediately. The browser may cancel the request mid-flight; if so the
  // 210s online threshold catches up shortly after.
  const onPageHide = () => {
    if (stopped) return;
    stopped = true;
    clearTimers();
    const target = channel;
    if (target) {
      target.publish({
        type: "presence",
        state: "offline",
        sessionId,
        at: new Date().toISOString(),
      }).catch(() => {});
    }
    markPresenceOffline(normalizedUserId);
  };

  void ensureChannel();
  if (isVisible()) {
    goVisible();
  } else {
    // Opened in a background tab: record last_seen once but don't announce
    // an online session that isn't really there.
    void sendHeartbeat({ force: true });
  }

  if (typeof document !== "undefined") {
    document.addEventListener("visibilitychange", onVisibility);
  }
  if (typeof window !== "undefined") {
    window.addEventListener("focus", onFocus);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);
  }

  return () => {
    const alreadyStopped = stopped;
    stopped = true;
    clearTimers();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", onVisibility);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
    }
    if (!alreadyStopped) {
      const target = channel;
      if (target) {
        target.publish({
          type: "presence",
          state: "offline",
          sessionId,
          at: new Date().toISOString(),
        }).catch(() => {});
      }
      // Explicit teardown (sign-out, route unmount) also marks the user
      // offline so peers don't keep seeing them as online.
      markPresenceOffline(normalizedUserId);
    }
    try {
      channel?.close?.();
    } catch {}
    channel = null;
  };
}

// ---------------------------------------------------------------------------
// Viewer side: refcounted per-user watchers shared by every screen that
// shows a presence dot, so Active + Past + Chat watching the same
// counterpart share one channel subscription.
// ---------------------------------------------------------------------------

const presenceWatchEntries = new Map();

function notifyPresenceListeners(userId, entry) {
  const snapshot = { online: entry.online === true, lastSeenAt: entry.lastSeenAt || null };
  entry.listeners.forEach((listenerRef) => {
    try {
      listenerRef.fn(userId, snapshot);
    } catch (error) {
      console.error("Presence watch listener failed:", error);
    }
  });
}

function setEntryOnline(userId, entry, online, atIso) {
  entry.lastSeenAt = maxIso(entry.lastSeenAt, atIso);
  if (entry.online === online) return;
  entry.online = online;
  notifyPresenceListeners(userId, entry);
}

function sweepEntrySessions(userId, entry) {
  if (!entry.sessions.size) return;
  const cutoffMs = Date.now() - PRESENCE_SESSION_STALE_MS;
  let newestDroppedMs = 0;
  entry.sessions.forEach((seenMs, sessionId) => {
    if (seenMs < cutoffMs) {
      entry.sessions.delete(sessionId);
      if (seenMs > newestDroppedMs) newestDroppedMs = seenMs;
    }
  });
  if (!entry.sessions.size && newestDroppedMs > 0) {
    setEntryOnline(userId, entry, false, new Date(newestDroppedMs).toISOString());
  }
}

function handlePresenceData(userId, entry, data) {
  const event = extractPresenceEvent(data);
  if (!event) return;
  const eventMs = Date.parse(event.at);
  const seenMs = Number.isFinite(eventMs) ? eventMs : Date.now();

  if (event.state === "online") {
    entry.sessions.set(event.sessionId, seenMs);
    setEntryOnline(userId, entry, true, event.at);
    return;
  }

  entry.sessions.delete(event.sessionId);
  if (!entry.sessions.size) {
    setEntryOnline(userId, entry, false, event.at);
  }
}

async function openWatchEntry(userId, entry) {
  try {
    const channel = await connectPresenceChannel(userId);
    if (entry.closed) {
      try {
        channel?.close?.();
      } catch {}
      return;
    }
    entry.channel = channel;
    entry.subscription = channel.subscribe({
      next: (data) => handlePresenceData(userId, entry, data),
      error: (error) => console.error("Presence subscription error:", { userId, error }),
    });
    entry.sweepTimerId = setInterval(
      () => sweepEntrySessions(userId, entry),
      PRESENCE_SESSION_SWEEP_MS
    );
  } catch (error) {
    // No live updates for this user; callers still have the
    // threshold-based status from their initial listPresenceByUserIds.
    console.error("Presence watch connect failed:", { userId, error });
  }
}

function closeWatchEntry(entry) {
  entry.closed = true;
  if (entry.sweepTimerId) {
    clearInterval(entry.sweepTimerId);
    entry.sweepTimerId = null;
  }
  try {
    entry.subscription?.unsubscribe?.();
  } catch {}
  try {
    entry.channel?.close?.();
  } catch {}
  entry.channel = null;
  entry.subscription = null;
}

/**
 * Watch live presence for a set of user ids. `onChange(userId, { online,
 * lastSeenAt })` fires only on transitions — seed initial state from
 * listPresenceByUserIds + isUserOnline. Returns an unsubscribe function.
 */
export function watchPresenceForUsers(userIds = [], onChange) {
  const uniqueIds = [...new Set((userIds || []).map(normalizeString).filter(Boolean))];
  if (!uniqueIds.length || typeof onChange !== "function") {
    return () => {};
  }

  // One wrapper per acquisition: the same callback registered through two
  // separate watch calls must not collapse into one Set entry, or the first
  // release would silence the survivor.
  const listenerRef = { fn: onChange };

  uniqueIds.forEach((userId) => {
    let entry = presenceWatchEntries.get(userId);
    if (!entry) {
      entry = {
        listeners: new Set(),
        sessions: new Map(),
        online: null,
        lastSeenAt: "",
        channel: null,
        subscription: null,
        sweepTimerId: null,
        closed: false,
      };
      presenceWatchEntries.set(userId, entry);
      void openWatchEntry(userId, entry);
    }
    entry.listeners.add(listenerRef);
    // Late joiners get the watcher's current knowledge immediately (it may
    // already have fresher state than the caller's last fetch).
    if (entry.online !== null) {
      try {
        onChange(userId, { online: entry.online, lastSeenAt: entry.lastSeenAt || null });
      } catch (error) {
        console.error("Presence watch listener failed:", error);
      }
    }
  });

  let released = false;
  return () => {
    if (released) return;
    released = true;
    uniqueIds.forEach((userId) => {
      const entry = presenceWatchEntries.get(userId);
      if (!entry) return;
      entry.listeners.delete(listenerRef);
      if (!entry.listeners.size) {
        closeWatchEntry(entry);
        presenceWatchEntries.delete(userId);
      }
    });
  };
}
